/**
 * The shared start sheet (spec 024 D-16, v17): one large modal layout that
 * the story start sheet (024, `StoryStartSheet`) wraps, and spec 029's scene
 * start sheet wraps too. It owns:
 *
 * - the brief field, which starts 8 rows tall, grows with its content up to
 *   60% of the window's height and then scrolls, and can be dragged taller
 *   (`resize: vertical`, never below 8 rows); a dragged height stays until
 *   the sheet closes;
 * - the soft character count under it (never blocks, shows no limit, D-17);
 * - the optional-field rows, each with a visible label that names it
 *   (Learning objective, Audience, and Length when the wrapper passes
 *   options);
 * - the unavailable state (agent status unavailable or failed);
 * - the modal conventions (focus, Cmd/Ctrl+Enter, cancel, busy, errors);
 * - the "Blank …" card below an "or" divider.
 *
 * The wrapper passes its texts, its test-id prefix and its two actions. An
 * action that throws shows its message as the sheet's error and keeps the
 * sheet open with everything typed in it.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { useAssistantStore } from '../../stores/assistantStore';

export const OBJECTIVE_MAX = 300;
export const AUDIENCE_MAX = 200;
/** The brief field's starting (and smallest) height, in rows. */
export const BRIEF_MIN_ROWS = 8;
/** The share of the window's height the brief field grows to before it scrolls. */
export const BRIEF_MAX_VIEWPORT_SHARE = 0.6;

export const START_SHEET_LABELS = {
  optionalHeading: 'Add details (optional)',
  objective: 'Learning objective (optional)',
  objectivePlaceholder: 'What should learners be able to do afterwards?',
  audience: 'Audience (optional)',
  length: 'Length',
  unavailable: "The assistant isn't set up",
  startWithAI: 'Start with AI',
} as const;

/** The longest name `nameFromBrief` gives (024 D-4). */
export const NAME_FROM_BRIEF_MAX = 60;

/**
 * The name of a story or scene started with AI (024 D-4, 029): the brief's
 * first non-empty line, trimmed to 60 characters at a word boundary.
 */
export function nameFromBrief(brief: string): string {
  const line = brief.split('\n').map((l) => l.trim()).find((l) => l !== '') ?? '';
  const flat = line.replace(/\s+/g, ' ');
  if (flat.length <= NAME_FROM_BRIEF_MAX) return flat;
  const cut = flat.slice(0, NAME_FROM_BRIEF_MAX + 1);
  const space = cut.lastIndexOf(' ');
  return (space > 0 ? cut.slice(0, space) : flat.slice(0, NAME_FROM_BRIEF_MAX)).trim();
}

/**
 * The brief field's height for a given content height: never below the
 * 8-row height, never above the maximum (60% of the window), the content
 * height in between.
 */
export function autoGrowHeight(contentHeight: number, minPx: number, maxPx: number): number {
  const upper = Math.max(minPx, maxPx);
  return Math.min(Math.max(contentHeight, minPx), upper);
}

/** The soft count under the brief: "0 characters", "1 character", "1,234 characters". */
export function characterCount(text: string): string {
  const n = text.length;
  return `${n.toLocaleString()} ${n === 1 ? 'character' : 'characters'}`;
}

export interface StartSheetValues {
  brief: string;
  objective: string;
  audience: string;
  /** The chosen Length option's value; absent when the sheet has no Length row. */
  length?: string;
}

export interface StartSheetProps {
  show: boolean;
  onHide: () => void;
  /** Prefix of every test id and element id (`story-start`, `scene-start`). */
  testIdPrefix: string;
  title: string;
  /** The brief's heading, which also names the brief field. */
  heading: string;
  placeholder: string;
  audiencePlaceholder: string;
  /** The Length row's options; no Length row without them. */
  lengthOptions?: ReadonlyArray<{ value: string; label: string }>;
  defaultLength?: string;
  blankTitle: string;
  blankHint: string;
  /** Starts with AI; throw to show the message and keep the sheet open. */
  onStart: (values: StartSheetValues) => Promise<void>;
  /** Creates the blank item; throw to show the message and keep the sheet open. */
  onBlank: () => Promise<void>;
}

const messageOf = (e: unknown) => (e instanceof Error ? e.message : String(e));

export const StartSheet: React.FC<StartSheetProps> = ({
  show, onHide, testIdPrefix: p, title, heading, placeholder, audiencePlaceholder,
  lengthOptions, defaultLength, blankTitle, blankHint, onStart, onBlank,
}) => {
  const status = useAssistantStore((s) => s.status);
  const loadStatus = useAssistantStore((s) => s.loadStatus);
  const firstLength = defaultLength ?? lengthOptions?.[0]?.value;
  const [brief, setBrief] = useState('');
  const [objective, setObjective] = useState('');
  const [audience, setAudience] = useState('');
  const [length, setLength] = useState<string | undefined>(firstLength);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const briefRef = useRef<HTMLTextAreaElement>(null);
  const blankRef = useRef<HTMLButtonElement>(null);
  // The brief field's sizing: its 8-row height (measured once per mount of
  // the field), the height last set by growth, and whether the author dragged it.
  const sizing = useRef<{ el: HTMLTextAreaElement | null; minPx: number; lastSet: number; dragged: boolean }>(
    { el: null, minPx: 0, lastSet: 0, dragged: false },
  );

  const unavailable = status === 'unavailable';
  const canStart = !unavailable && !busy && brief.trim().length > 0;

  useEffect(() => { if (show) void loadStatus(); }, [show, loadStatus]);
  useEffect(() => {
    if (show) setError(null);
    else sizing.current = { el: null, minPx: 0, lastSet: 0, dragged: false };
  }, [show]);

  // Grow the brief field with its content (D-16). jsdom has no layout, so a
  // field with no measured height is left alone.
  useLayoutEffect(() => {
    const el = briefRef.current;
    if (!show || !el) return;
    const s = sizing.current;
    if (s.el !== el) {
      el.style.height = '';
      s.el = el;
      s.minPx = el.offsetHeight;
      s.lastSet = 0;
      s.dragged = false;
      if (s.minPx > 0) el.style.minHeight = `${s.minPx}px`;
    }
    if (s.minPx <= 0 || s.dragged) return;
    const border = el.offsetHeight - el.clientHeight;
    el.style.height = `${s.minPx}px`;
    const next = autoGrowHeight(el.scrollHeight + border, s.minPx, window.innerHeight * BRIEF_MAX_VIEWPORT_SHARE);
    el.style.height = `${next}px`;
    s.lastSet = el.offsetHeight;
  }, [brief, show]);

  // A height the author drags to stays until the sheet closes.
  useEffect(() => {
    const el = briefRef.current;
    if (!show || !el || typeof ResizeObserver !== 'function') return undefined;
    const ro = new ResizeObserver(() => {
      const s = sizing.current;
      if (s.el === el && s.lastSet > 0 && Math.abs(el.offsetHeight - s.lastSet) > 1) s.dragged = true;
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [show]);

  const focusFirst = () => {
    if (unavailable) blankRef.current?.focus();
    else briefRef.current?.focus();
  };
  // Status may settle after the sheet opened.
  useEffect(() => { if (show) focusFirst(); }, [show, unavailable]); // eslint-disable-line react-hooks/exhaustive-deps

  const clear = () => { setBrief(''); setObjective(''); setAudience(''); setLength(firstLength); };

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      clear();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const start = () => {
    if (!canStart) return;
    void run(() => onStart({ brief, objective, audience, ...(lengthOptions ? { length } : {}) }));
  };
  const blank = () => {
    if (busy) return;
    void run(onBlank);
  };

  const fieldsDisabled = unavailable || busy;
  const id = (s: string) => `${p}-${s}`;

  return (
    <Modal
      show={show}
      onHide={() => { if (!busy) onHide(); }}
      onEntered={focusFirst}
      animation={false}
      centered
      size="lg"
      data-testid={`${p}-sheet`}
      aria-labelledby={id('sheet-title')}
    >
      <Modal.Header closeButton>
        <Modal.Title id={id('sheet-title')}>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" data-testid={id('error')}>{error}</Alert>}
        <section aria-labelledby={id('ai-heading')} className="mb-3">
          <h5 id={id('ai-heading')} className="mb-2">{heading}</h5>
          {unavailable && (
            <p className="text-muted small mb-2" data-testid={id('unavailable')}>{START_SHEET_LABELS.unavailable}</p>
          )}
          <Form.Control
            as="textarea"
            ref={briefRef}
            rows={BRIEF_MIN_ROWS}
            value={brief}
            placeholder={placeholder}
            disabled={fieldsDisabled}
            aria-labelledby={id('ai-heading')}
            data-testid={id('brief')}
            style={{ resize: 'vertical', overflowY: 'auto' }}
            onChange={(e) => setBrief(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                start();
              }
            }}
          />
          <div className="text-end text-muted small mt-1" data-testid={id('count')} aria-live="polite">
            {characterCount(brief)}
          </div>

          <h6 className="text-muted small fw-normal mt-2 mb-2">{START_SHEET_LABELS.optionalHeading}</h6>
          <Form.Group className="mb-2" controlId={id('objective-field')}>
            <Form.Label className="small mb-1">{START_SHEET_LABELS.objective}</Form.Label>
            <Form.Control
              maxLength={OBJECTIVE_MAX}
              value={objective}
              placeholder={START_SHEET_LABELS.objectivePlaceholder}
              disabled={fieldsDisabled}
              data-testid={id('objective')}
              onChange={(e) => setObjective(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-2" controlId={id('audience-field')}>
            <Form.Label className="small mb-1">{START_SHEET_LABELS.audience}</Form.Label>
            <Form.Control
              maxLength={AUDIENCE_MAX}
              value={audience}
              placeholder={audiencePlaceholder}
              disabled={fieldsDisabled}
              data-testid={id('audience')}
              onChange={(e) => setAudience(e.target.value)}
            />
          </Form.Group>
          {lengthOptions && (
            <Form.Group className="mb-2" controlId={id('length-field')}>
              <Form.Label className="small mb-1">{START_SHEET_LABELS.length}</Form.Label>
              <Form.Select
                value={length}
                disabled={fieldsDisabled}
                data-testid={id('length')}
                onChange={(e) => setLength(e.target.value)}
                style={{ width: '33%', minWidth: 200 }}
              >
                {lengthOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Form.Select>
            </Form.Group>
          )}
          <Button className="mt-3 w-100" variant="primary" disabled={!canStart} onClick={start} data-testid={id('ai')}>
            {START_SHEET_LABELS.startWithAI}
          </Button>
        </section>
        <div className="d-flex align-items-center gap-2 my-3 text-muted small" aria-hidden="true">
          <hr className="flex-grow-1 m-0" /> or <hr className="flex-grow-1 m-0" />
        </div>
        <button
          type="button"
          ref={blankRef}
          className="btn btn-outline-secondary w-100 text-start p-3"
          disabled={busy}
          onClick={blank}
          data-testid={id('blank')}
        >
          <strong className="d-block">{blankTitle}</strong>
          <span className="small text-muted">{blankHint}</span>
        </button>
      </Modal.Body>
    </Modal>
  );
};

export default StartSheet;
